const math = require('mathjs');
const { assert } = require('../common/assert');
const { BaseBinGeometry } = require('./base-bin-geometry');
const { CircularContainer } = require('./circular-container');
const { degreesToRadians } = require('../degrees');
const { makePointCloud } = require('../point-cloud');
const { XYCircle } = require('./shapes');
const { isSurface } = require('../surface');
const Transform = require('../transform');

const BRACKET_TO_CAMERA_DISTANCE = 0.051; /* m */

exports.CircularBinGeometry = class CircularBinGeometry extends BaseBinGeometry {
  constructor(bin) {
    super(bin, circularBinContainer(bin));
  }

  get isCircular() {
    return true;
  }

  distanceToBin([x, y, z]) {
    // TODO it would be nice to reimplement this using circular-container and not assuming circles of the same centre

    // First, we are going to convert from [x, y, z] co-ordinate system to [r, h], where r is the distance from the
    // center of the bin, and h is just the height above the bottom of the hopper (i.e. z). This is going to simplify the
    // rest of the math since we'll be able to do all our of calculations in 2 dimensions instead of 3.
    const [r, h] = binCoordinateToCylindricalCoordinate(x, y, z);
    const binGeometry = this.#binGeometryLineSegments();

    // Return the distance to the closest line segment to [r, h]
    return Math.min(...binGeometry.map(segment => cartesianDistanceToLineSegment2D([r, h], segment)));
  }

  bracketAttachmentPoint() {
    if (this.bin.dimensions.hatchType === 'side') {
      // Side hatch case:
      const totalTopDistance = this.bin.dimensions.sideHatch.topDistance + this.bin.dimensions.sideHatch.radius * (1 + Math.cos(degreesToRadians(this.bin.dimensions.sideHatch.devicePlacement) - Math.PI));
      const topperAngle = Math.atan((this.bin.dimensions.body.radius - this.bin.dimensions.top.truncatedRadius) / this.bin.dimensions.top.height);
      const topOfBinToBracketAttachmentPoint = totalTopDistance * Math.cos(topperAngle);
      const radiusToBracket = this.bin.dimensions.top.truncatedRadius + totalTopDistance * Math.sin(topperAngle);
      return [radiusToBracket, 0, this.bin.dimensions.totalHeight - topOfBinToBracketAttachmentPoint + this.bin.dimensions.lipHeight];
    }
    // Normal case:
    return [this.bin.dimensions.top.truncatedRadius /* m */, 0, this.bin.dimensions.lipHeight + this.bin.dimensions.totalHeight];
  }

  bracketToCameraVector() {
    if (!this.bin.dimensions.sideHatch) {
      return [BRACKET_TO_CAMERA_DISTANCE, 0, -this.bin.dimensions.deviceDepth];
    }

    // Side hatch case:
    return [-BRACKET_TO_CAMERA_DISTANCE * Math.cos(degreesToRadians(this.bin.dimensions.sideHatch.devicePlacement)), 0, -this.bin.dimensions.deviceDepth];
  }

  bracketRotationForBin() {
    switch (this.bin.dimensions.hatchType) {
    case 'side':
      return degreesToRadians(this.bin.dimensions.sideHatch.devicePlacement - 180);
    default:
      return 0;
    }
  }

  sensorMidpoint() {
    const [bX, bY, bZ] = this.bracketAttachmentPoint();
    const [cX, cY, cZ] = this.bracketToCameraVector();
    return [bX + cX, bY + cY, bZ + cZ];
  }

  estimatedCameraAngleForBin() {
    // Create a surface with a single point in it representing the camera in the bin's co-ordinate system: (0, 0, 0) at bottom of hopper
    const bracketPoint = this.bracketAttachmentPoint();
    const bracketRotation = this.bracketRotationForBin();

    const [x, y, z] = makePointCloud([bracketPoint[0]], [bracketPoint[1]], [bracketPoint[2]])
      .transform(Transform.rotate(0, 0, bracketRotation))
      .point(0);

    return ({
      tilt: Math.atan(y / z),
      pan: Math.atan(x / z),
    });
  }

  binXYCoordinateToHeightMapCoordinate([x, y], resolution) {
    return [this.#binCoordToHeightMapCoord(x, resolution), this.#binCoordToHeightMapCoord(y, resolution)];
  }

  heightMapXYCoordinateToBinCoordinate([x, y], resolution) {
    return [this.#heightMapCoordToBinCoord(x, resolution), this.#heightMapCoordToBinCoord(y, resolution)];
  }

  isHeightMapCoordinateInBin(x, y, resolution) {
    return x >= 0 && x < resolution && y >= 0 && y < resolution
      && math.distance([(resolution - 1) / 2, (resolution - 1) / 2], [x, y]) <= resolution / 2;
  }

  metresPerHeightMapPixel(resolution) {
    const binRadius = this.bin.dimensions.body.radius;
    return (2 * binRadius) / resolution;
  }

  /**
   * Changes a surface from the perspective of the camera to the perspective of the bin.
   * The resulting surface has (0, 0, 0) being the center of the bottom of the hopper.
   *
   * @param {Surface} surface
   * @param {number} tilt
   * @param {number} pan
   * @return {Surface}
   */
  changeSurfaceToBinsCoordinateSystem(surface, tilt = 0, pan = 0) {
    if (!isSurface(surface)) {
      throw new Error('not a surface');
    }

    // Rotate the surface (using pan and tilt parameters) around where the bracket attaches to the bin
    const cameraTransform = Transform.translate(0, 0, -this.bin.dimensions.deviceDepth)
      .then(Transform.rotate(tilt, pan, 0))
      .then(Transform.translate(0, 0, this.bin.dimensions.deviceDepth))
      // Right now (0, 0, 0) is positioned at the position of the camera, with negative z values being below the camera
      // So reposition the surface over the center of the bin and move the surface down so that (0, 0, 0) is the center of
      // the bottom of the hopper
      .then(Transform.rotate(0, 0, this.bracketRotationForBin()))
      .then(Transform.translate(...this.bracketAttachmentPoint(), 0, 0))
      .then(Transform.translate(...this.bracketToCameraVector(), 0, 0));

    return surface.transform(cameraTransform);
  }

  /**
   * Describes a bin as line segments in the [r, h] co-ordinate space
   */
  #binGeometryLineSegments() {
    const { bin } = this;
    // Define some points in the [r, h] co-ordinate system
    const bottomOfHopper = [bin.dimensions.hopper.truncatedRadius, 0];
    const hopperBodyIntersection = [bin.dimensions.body.radius, bin.dimensions.hopper.height];
    const bodyTopperIntersection = [bin.dimensions.body.radius, bin.dimensions.hopper.height + bin.dimensions.body.height];
    const topOfTopper = [bin.dimensions.top.truncatedRadius, bin.dimensions.hopper.height + bin.dimensions.body.height + bin.dimensions.top.height];

    // Now we're going to define some line segments in the [r, h] co-ordinate system that define the bin walls.
    const hopperWall = [bottomOfHopper, hopperBodyIntersection];
    const bodyWall = [hopperBodyIntersection, bodyTopperIntersection];
    const topperWall = [bodyTopperIntersection, topOfTopper];

    return [hopperWall, bodyWall, topperWall];
  }


  #binCoordToHeightMapCoord(coordinate /* m */, resolution) {
    const binRadius = this.bin.dimensions.body.radius;
    return (((coordinate + binRadius) / (binRadius * 2)) * resolution);
  }

  #heightMapCoordToBinCoord(coordinate /* no unit */, resolution) {
    const binRadius = this.bin.dimensions.body.radius;
    return (coordinate / resolution) * (binRadius * 2) - binRadius;
  }
};


function binCoordinateToCylindricalCoordinate(x, y, z) {
  return [Math.sqrt(x ** 2 + y ** 2), z];
}

// The following is adapted from this StackOverflow answer: https://stackoverflow.com/a/6853926
function cartesianDistanceToLineSegment2D([x, y], [[x1, y1], [x2, y2]]) {
  const A = x - x1;
  const B = y - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;
  if (lenSq !== 0) { // in case of 0 length line
    param = dot / lenSq;
  }

  let xx;
  let yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = x - xx;
  const dy = y - yy;
  return Math.sqrt(dx * dx + dy * dy);
}

function circularBinContainer(bin) {
  assert(bin.dimensions.shape === 'circular', 'Bin must be circular');
  const { lipHeight, top, body, hopper } = bin.dimensions;
  const circles = [
    // top to bottom
    ...lipHeight ? [
      new XYCircle({ cx: 0, cy: 0, r: top.truncatedRadius, z: hopper.height + body.height + top.height + lipHeight }),
    ] : [],
    new XYCircle({ cx: 0, cy: 0, r: top.truncatedRadius, z: hopper.height + body.height + top.height }),
    new XYCircle({ cx: 0, cy: 0, r: body.radius, z: hopper.height + body.height }),
    new XYCircle({ cx: 0, cy: 0, r: body.radius, z: hopper.height }),
    new XYCircle({ cx: 0, cy: 0, r: hopper.truncatedRadius, z: 0 }),
  ];
  return new CircularContainer(circles);
}
